import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AlertTriangle, X } from 'lucide-react';
import { cn, Dialog, DialogContent, DialogClose, DialogTitle, Button } from '@scio3/ui';
import { FREE_PLAN_LIMITS } from '@scio3/shared-types';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { ACCOUNT_MEMBERS_PATH } from '@/lib/routes';

/**
 * #1460 — Standalone warning dialog shown when a trial OR paid account has
 * > FREE_PLAN_LIMITS.MAX_USER members and clicks "Downgrade to Free Plan".
 * Opened by handleSelectFree in useSubscriptionWizard BEFORE the survey.
 */
export function MemberDowngradeWarningDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = useBillingDialogStore((s) => s.memberDowngradeWarningOpen);
  const memberCount = useBillingDialogStore((s) => s.memberDowngradeWarningCount);
  const close = useBillingDialogStore((s) => s.closeMemberDowngradeWarning);
  const closeWizard = useBillingDialogStore((s) => s.closeDialog);

  const handleGoToMembers = useCallback(() => {
    close();
    closeWizard();
    navigate(ACCOUNT_MEMBERS_PATH);
  }, [close, closeWizard, navigate]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
        hideCloseButton
        data-testid="member-downgrade-warning-dialog"
      >
        <div className="flex items-center justify-between bg-amber-500 px-5 py-3 text-white">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-white">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            {t('Cannot downgrade to Free Plan')}
          </DialogTitle>
          <DialogClose
            className={cn(
              'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md',
              'text-white/80 transition-colors',
              'hover:bg-white/15 hover:text-white',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
            )}
            aria-label={t('Close')}
          >
            <X className="h-4 w-4" />
          </DialogClose>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="text-sm">
            {t('freePlanMemberDowngradeWarning', {
              count: memberCount,
              max: FREE_PLAN_LIMITS.MAX_USER,
            })}
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-muted/30 px-5 py-3">
          <Button variant="outline" onClick={close} className="cursor-pointer">
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleGoToMembers}
            className="cursor-pointer"
            data-testid="member-downgrade-warning-go-btn"
          >
            {t('Go to Account Members')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
